import React, { useMemo } from 'react';
import { Row, Col, Alert, Table, Spin } from 'antd';
import type { EChartsOption } from 'echarts';
import ChartCard from '@/components/ChartCard';
import { StorageExecChart, StoragePriceChart } from './StorageDetailCharts';
import RevenueBatteryChart from './RevenueBatteryChart';
import { format2 } from '@/utils/charts';
import type { PvDailyRow, StorageDailyRow, AGCStorageDetailRow } from '@/types/api';

interface RevenueSummaryTabProps {
  pvRows: PvDailyRow[];
  storageRows: StorageDailyRow[];
  pvMissing: string[];
  storageMissing: string[];
  monthPdiffMap: Record<string, number>;
  monthMtdMap: Record<string, number>;
  isSingleDay: boolean;
  singleDayDetailRows?: AGCStorageDetailRow[];
  singleDayLoading?: boolean;
  singleDayError?: Error | null;
  onSingleDayRetry?: () => void;
  batteryChartData?: Record<string, AGCStorageDetailRow[]>;
  batteryChartLoading?: boolean;
  interprovPeriods?: Array<{period: string; power: number; price: number}>;
  agcPdiff?: number;
}

const RevenueSummaryTab: React.FC<RevenueSummaryTabProps> = ({
  pvRows,
  storageRows,
  pvMissing,
  storageMissing,
  isSingleDay,
  singleDayDetailRows,
  singleDayLoading,
  singleDayError,
  onSingleDayRetry,
  batteryChartData,
  batteryChartLoading,
  interprovPeriods,
  agcPdiff,
}) => {
  const merged = useMemo(() => {
    const map = new Map<string, {
      date: string;
      pvRt: number;
      pvSub: number;
      pvIp: number;
      pvTotal: number;
      pvEnergy: number;
      v: number;
      e: number;
      s: number;
      a: number;
      stTotal: number;
      stEnergy: number;
    }>();

    for (const r of pvRows) {
      const d = typeof r['日期'] === 'string' ? r['日期'] : String(r['日期']);
      map.set(d, {
        date: d,
        pvRt: Number(r['实时电费(元)']) || 0,
        pvSub: Number(r['场外补贴电费(元)']) || 0,
        pvIp: Number(r['省间现货电费(元)']) || 0,
        pvTotal: Number(r['总收益(元)']) || 0,
        pvEnergy: Number(r['总上网电量(MWh)']) || 0,
        v: 0, e: 0, s: 0, a: 0, stTotal: 0, stEnergy: 0,
      });
    }

    for (const r of storageRows) {
      const d = typeof r['日期'] === 'string' ? r['日期'] : String(r['日期']);
      const existing = map.get(d);
      const v = Number(r['虚报挽回(元)']) || 0;
      const e = Number(r['现货净利(元)']) || 0;
      const s = Number(r['机制补贴(元)']) || 0;
      const a = Number(r['规避考核(元)']) || 0;
      const stEnergy = Number(r['总放电量(MWh)']) || 0;
      const stTotal = e + s;
      if (existing) {
        existing.v = v; existing.e = e; existing.s = s; existing.a = a;
        existing.stTotal = stTotal; existing.stEnergy = stEnergy;
      } else {
        map.set(d, {
          date: d,
          pvRt: 0, pvSub: 0, pvIp: 0, pvTotal: 0, pvEnergy: 0,
          v, e, s, a, stTotal, stEnergy,
        });
      }
    }

    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [pvRows, storageRows]);

  const totals = useMemo(() => {
    let totalPv = 0, totalSt = 0, totalIp = 0, totalAll = 0;
    let totalPvEnergy = 0, totalStEnergy = 0;
    for (const r of merged) {
      totalIp += r.pvIp;
      totalPv += r.pvTotal;
      totalSt += r.stTotal;
      totalAll += r.pvTotal + r.stTotal;
      totalPvEnergy += r.pvEnergy;
      totalStEnergy += r.stEnergy;
    }
    const totalEnergy = totalPvEnergy + totalStEnergy;
    return {
      totalPv,
      totalSt,
      totalIp,
      totalAll,
      totalEnergy,
      totalPvEnergy,
      totalStEnergy,
      totalPrice: totalEnergy ? totalAll / totalEnergy : 0,
      pvPrice: totalPvEnergy ? totalPv / totalPvEnergy : 0,
      stPrice: totalStEnergy ? totalSt / totalStEnergy : 0,
    };
  }, [merged]);

  const xLabels = useMemo(() => merged.map((r) => {
    const d = new Date(r.date + 'T00:00:00');
    return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }), [merged]);

  const trendOption: EChartsOption = useMemo(() => {
    if (!merged.length) return {};
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, formatter: (params: any) => `${params[0].name}<br/>${params[0].seriesName}: ${format2(params[0].value)} 元` },
      toolbox: { show: false }, dataZoom: { show: false },
      grid: { left: 50, right: 20, top: 30, bottom: 85 },
      xAxis: { type: 'category', data: xLabels, axisLabel: { fontSize: 11, color: '#9ca3af' } },
      yAxis: { type: 'value', axisLabel: { fontSize: 11, color: '#9ca3af' }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
      series: [{ name: '全场总收益', type: 'line', data: merged.map((r) => r.pvTotal + r.stTotal), smooth: true, lineStyle: { color: '#0d9488', width: 2.5 }, areaStyle: { color: 'rgba(13,148,136,0.15)' }, itemStyle: { color: '#0d9488' }, showSymbol: false }],
    };
  }, [merged, xLabels]);

  const pieOption: EChartsOption = useMemo(() => {
    const data = [
      { name: '光伏实时电费', value: merged.reduce((s, r) => s + r.pvRt, 0) },
      { name: '光伏省间现货', value: merged.reduce((s, r) => s + r.pvIp, 0) },
      { name: '光伏场外补贴', value: merged.reduce((s, r) => s + r.pvSub, 0) },
      { name: '储能实时收入', value: merged.reduce((s, r) => s + r.e, 0) },
      { name: '储能补贴', value: merged.reduce((s, r) => s + r.s, 0) },
    ].filter((d) => Number.isFinite(d.value) && d.value !== 0);
    if (!data.length) return {};
    return {
      tooltip: { trigger: 'item', formatter: (params: any) => `${params.name}: ${format2(params.value)} 元` },
      legend: { orient: 'vertical', right: 10, top: 'center', textStyle: { fontSize: 11, color: '#374151' } },
      series: [{ type: 'pie', radius: ['35%', '55%'], center: ['28%', '50%'], data, label: { show: false }, emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.15)' } } }],
    };
  }, [merged]);

  const barOption: EChartsOption = useMemo(() => {
    if (!merged.length) return {};
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, formatter: (params: any) => { let res = params[0].name; for (const p of params) res += `<br/>${p.seriesName}: ${format2(p.value)} MWh`; return res; } },
      toolbox: { show: false }, dataZoom: { show: false },
      grid: { left: 50, right: 20, top: 30, bottom: 85 },
      xAxis: { type: 'category', data: xLabels, axisLabel: { fontSize: 11, color: '#9ca3af' } },
      yAxis: { type: 'value', name: '电量 (MWh)', axisLabel: { fontSize: 11, color: '#9ca3af' }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
      series: [
        { name: '光伏上网电量', type: 'bar', stack: 'total', data: merged.map((r) => r.pvEnergy), itemStyle: { color: '#0d9488' } },
        { name: '配储放电量', type: 'bar', stack: 'total', data: merged.map((r) => r.stEnergy), itemStyle: { color: '#374151' } },
      ],
    };
  }, [merged, xLabels]);

  // ── 单日 24 小时收益瀑布 ──
  // 15min窗口聚合: 省间cap按窗口, avgPrice按窗口加权, 与日汇总完全一致
  const hourBuckets = useMemo(() => {
    if (!isSingleDay) return [];
    const buckets = Array.from({ length: 24 }, () => ({ pvE: 0, pvRev: 0, e: 0, s: 0 }));
    const ipMap: Record<string, { power: number; price: number }> = {};
    for (const ip of interprovPeriods ?? []) ipMap[ip.period] = { power: ip.power, price: ip.price };
    const pdiff = agcPdiff ?? 0;
    const rows = singleDayDetailRows ?? [];

    for (let i = 0; i < rows.length; i += 3) {
      const r0 = rows[i];
      if (!r0) continue;
      const hh = parseInt(r0.time.slice(0, 2), 10);
      if (Number.isNaN(hh)) continue;
      const min0 = parseInt(r0.time.slice(3, 5), 10);
      const period = `${String(hh).padStart(2, '0')}:${String(Math.floor(min0 / 15) * 15).padStart(2, '0')}`;

      let wGrid = 0, wGridPrice = 0, wE = 0, wS = 0;
      for (let j = 0; j < 3 && i + j < rows.length; j++) {
        const r = rows[i + j];
        const gmw = (r.agc_act || 0) - Math.max(0, r.bat || 0);
        const mwh = gmw * (5 / 60);
        wGrid += mwh;
        wGridPrice += mwh * (r.p || 0);
        wE += r.e || 0;
        wS += r.s || 0;
      }

      const ip = ipMap[period];
      const ipEnergy = ip ? ip.power / 4 : 0;
      const ipCapped = Math.min(ipEnergy, Math.max(0, wGrid));
      const avgP = wGrid !== 0 ? wGridPrice / wGrid : 0;
      const internal = wGrid - ipCapped;

      buckets[hh].pvRev += internal * (avgP + pdiff) + ipCapped * (ip?.price || 0);
      buckets[hh].pvE += wGrid;
      buckets[hh].e += wE;
      buckets[hh].s += wS;
    }
    return buckets;
  }, [isSingleDay, singleDayDetailRows, interprovPeriods, agcPdiff]);

  const hourlyWaterfallOption: EChartsOption = useMemo(() => {
    if (!isSingleDay || !hourBuckets.length) return {};
    const hourlyValues = hourBuckets.map((b) => b.pvRev + b.e + b.s);
    if (hourlyValues.every((v) => v === 0)) return {};
    const totalAll = hourlyValues.reduce((s, v) => s + v, 0);
    const helpers = hourlyValues.map((_, i) => i === 0 ? 0 : hourlyValues.slice(0, i).reduce((a, b) => a + b, 0));
    helpers.push(0);
    const categories = [...Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}时`), '合计'];
    const values = [...hourlyValues, totalAll];
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, formatter: (params: any) => { const p = params[1] || params[0]; if (!p) return ''; const idx = p.dataIndex; const isTotal = idx === values.length - 1; return `${categories[idx]}<br/>${isTotal ? '合计' : '收益'}: ${format2(values[idx])} 元`; } },
      toolbox: { show: false }, dataZoom: { show: false },
      grid: { left: 60, right: 30, top: 30, bottom: 60 },
      xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 11, color: '#9ca3af' } },
      yAxis: { type: 'value', name: '元', axisLabel: { fontSize: 11, color: '#9ca3af' }, splitLine: { lineStyle: { color: '#f3f4f6' } } },
      series: [
        { name: '辅助', type: 'bar', stack: 'total', itemStyle: { color: 'transparent' }, data: helpers },
        { name: '收益', type: 'bar', stack: 'total', data: hourlyValues.map((v, i) => ({ value: v, itemStyle: { color: i === categories.length - 1 ? '#0d9488' : v >= 0 ? '#0d9488' : '#ef4444' } })), label: { show: true, position: 'top', formatter: (p: any) => format2(p.value), fontSize: 10 } },
      ],
    };
  }, [isSingleDay, hourBuckets]);

  const energyPieOption: EChartsOption = useMemo(() => {
    if (!isSingleDay) return {};
    const pvE = Number(pvRows[0]?.['总上网电量(MWh)']) || 0;
    const stE = Number(storageRows[0]?.['总放电量(MWh)']) || 0;
    const data = [{ name: '光伏上网电量', value: pvE, itemStyle: { color: '#0d9488' } }, { name: '储能放电量', value: stE, itemStyle: { color: '#374151' } }].filter((d) => Number.isFinite(d.value) && d.value > 0);
    if (!data.length) return {};
    return { tooltip: { trigger: 'item', formatter: (p: any) => `${p.name}: ${format2(p.value)} MWh (${p.percent}%)` }, legend: { orient: 'vertical', right: 10, top: 'center', textStyle: { fontSize: 11, color: '#374151' } }, series: [{ type: 'pie', radius: ['35%', '55%'], center: ['28%', '50%'], data, label: { show: false }, emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.15)' } } }] };
  }, [isSingleDay, pvRows, storageRows]);

  const tableColumns = [
    { title: '日期', dataIndex: 'date', key: 'date' },
    { title: '总上网电量', dataIndex: 'totalEnergy', key: 'totalEnergy', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '总实时电费', dataIndex: 'totalRt', key: 'totalRt', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '总机制补偿', dataIndex: 'totalSub', key: 'totalSub', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '总收入', dataIndex: 'totalAll', key: 'totalAll', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '光伏总收入', dataIndex: 'pvTotal', key: 'pvTotal', align: 'right' as const, render: (v: number) => format2(v) },
    { title: '储能总收入', dataIndex: 'stTotal', key: 'stTotal', align: 'right' as const, render: (v: number) => format2(v) },
  ];

  const tableData = useMemo(() => {
    const rows = merged.map((r) => ({ key: r.date, date: r.date, totalEnergy: r.pvEnergy + r.stEnergy, totalRt: r.pvRt + r.e, totalSub: r.pvSub + r.s, totalAll: r.pvTotal + r.stTotal, pvTotal: r.pvTotal, stTotal: r.stTotal }));
    const sum = { key: 'sum', date: '合计', totalEnergy: rows.reduce((s, r) => s + r.totalEnergy, 0), totalRt: rows.reduce((s, r) => s + r.totalRt, 0), totalSub: rows.reduce((s, r) => s + r.totalSub, 0), totalAll: rows.reduce((s, r) => s + r.totalAll, 0), pvTotal: rows.reduce((s, r) => s + r.pvTotal, 0), stTotal: rows.reduce((s, r) => s + r.stTotal, 0) };
    return [...rows, sum];
  }, [merged]);

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}><div style={{ textAlign: 'center', padding: '16px 0' }}><div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>全场总收益</div><div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalAll)} 元</div><div style={{ marginTop: 8, display: 'inline-block', textAlign: 'left' }}><div style={{ display: 'flex', justifyContent: 'space-between', width: 170, fontSize: 12, color: '#6b7280' }}><span style={{ color: '#9ca3af' }}>光伏</span><span>{format2(totals.totalPv)} 元</span></div><div style={{ display: 'flex', justifyContent: 'space-between', width: 170, fontSize: 12, color: '#6b7280', marginTop: 2 }}><span style={{ color: '#9ca3af' }}>配储</span><span>{format2(totals.totalSt)} 元</span></div></div></div></Col>
        <Col xs={24} sm={8}><div style={{ textAlign: 'center', padding: '16px 0' }}><div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>总上网电量</div><div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalEnergy)} MWh</div><div style={{ marginTop: 8, display: 'inline-block', textAlign: 'left' }}><div style={{ display: 'flex', justifyContent: 'space-between', width: 170, fontSize: 12, color: '#6b7280' }}><span style={{ color: '#9ca3af' }}>光伏</span><span>{format2(totals.totalPvEnergy)} MWh</span></div><div style={{ display: 'flex', justifyContent: 'space-between', width: 170, fontSize: 12, color: '#6b7280', marginTop: 2 }}><span style={{ color: '#9ca3af' }}>配储</span><span>{format2(totals.totalStEnergy)} MWh</span></div></div></div></Col>
        <Col xs={24} sm={8}><div style={{ textAlign: 'center', padding: '16px 0' }}><div style={{ fontSize: '0.75rem', color: '#9ca3af', marginBottom: 4 }}>综合电价</div><div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#374151' }}>{format2(totals.totalPrice)} 元/MWh</div><div style={{ marginTop: 8, display: 'inline-block', textAlign: 'left' }}><div style={{ display: 'flex', justifyContent: 'space-between', width: 170, fontSize: 12, color: '#6b7280' }}><span style={{ color: '#9ca3af' }}>光伏</span><span>{format2(totals.pvPrice)} 元/MWh</span></div><div style={{ display: 'flex', justifyContent: 'space-between', width: 170, fontSize: 12, color: '#6b7280', marginTop: 2 }}><span style={{ color: '#9ca3af' }}>配储</span><span>{format2(totals.stPrice)} 元/MWh</span></div></div></div></Col>
      </Row>

      {isSingleDay ? (<>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}><Col xs={24}><ChartCard title="24小时收益瀑布" option={hourlyWaterfallOption} height={350} /></Col></Row>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}><Col xs={24} lg={12}><ChartCard title="电量结构占比" option={energyPieOption} height={300} /></Col><Col xs={24} lg={12}><ChartCard title="收益结构占比" option={pieOption} height={300} /></Col></Row>
        <div style={{ marginBottom: 24 }}><StorageExecChart rows={singleDayDetailRows || []} loading={singleDayLoading} error={singleDayError} onRetry={onSingleDayRetry} height={400} /></div>
        <div style={{ marginBottom: 24 }}><StoragePriceChart rows={singleDayDetailRows || []} loading={singleDayLoading} error={singleDayError} onRetry={onSingleDayRetry} height={400} /></div>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 16 }}>按日汇总表</div>
        <Table columns={tableColumns} dataSource={tableData} pagination={false} size="small" bordered />
      </>) : (<>
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}><Col xs={24} lg={10}><ChartCard title="全场站日收益趋势" option={trendOption} height={300} /></Col><Col xs={24} lg={6}><ChartCard title="收益结构占比" option={pieOption} height={300} /></Col><Col xs={24} lg={8}><ChartCard title="电量结构对比" option={barOption} height={300} /></Col></Row>
        <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 16 }}>按日汇总表</div>
        <Table columns={tableColumns} dataSource={tableData} pagination={false} size="small" bordered />
      </>)}

      {batteryChartData && Object.keys(batteryChartData).length > 0 && (<div style={{ marginBottom: 24 }}>{batteryChartLoading && <Spin style={{ display: 'block', marginBottom: 8 }} />}<RevenueBatteryChart data={batteryChartData} height={400} /></div>)}
      {pvMissing.length > 0 && (<Alert message={`光伏：以下日期缺失相关数据，已跳过：${pvMissing.join('，')}`} type="warning" showIcon style={{ marginTop: 16 }} />)}
      {storageMissing.length > 0 && (<Alert message={`配储：以下日期缺失关键数据，已略过：${storageMissing.join('，')}`} type="info" showIcon style={{ marginTop: 16 }} />)}
    </div>
  );
};

export default RevenueSummaryTab;
